import {sharedCampaigns} from '@cl/shared';
import conditionsApi from 'common/src/modules/conditions/conditions.api';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../../campaignSetupAgent.enums';
import {ConditionCardScratchpadState} from '../../../../standaloneAgents/promptToCardAgentV2/scratchpad/conditionCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {getFeedIdFromNode} from '../../../../tools/conditions/tools/searchFeedValuesTool/utils/getFeedInfoFromNode';
import {getFeedsFromPreviousCards} from '../../../../tools/conditions/tools/searchFeedValuesTool/utils/getFeedsFromPreviousCards';

const MAX_RESULTS = 120;

const searchFeedValuesToolSchema = z.object({
  cardId: z.number().describe('The ID of the current condition card'),
  feedIds: z
    .array(z.number())
    .optional()
    .nullable()
    .describe(
      'Optional feed IDs if extracted from user prompt. If provided, this will be validated and returned. If not provided, the tool will search using waterfall logic. You can provide multiple feed IDs to search for values from multiple feeds.',
    ),
  valueContainsString: z
    .string()
    .optional()
    .nullable()
    .describe('Optional filter string to search for values that contain this substring'),
  maxResults: z
    .number()
    .optional()
    .nullable()
    .default(MAX_RESULTS)
    .describe('Optional maximum number of results to return'),
});

export default function searchFeedValuesTool() {
  async function execute({
    input: {cardId, feedIds = [], valueContainsString, maxResults},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<
    typeof searchFeedValuesToolSchema,
    ConditionCardScratchpadState
  >): Promise<ToolResult> {
    try {
      const validatedScratchpad = validateAgentScratchpad<ConditionCardScratchpadState>(
        agentScratchpad,
        NodeType.CONDITION,
      );

      // If the signal does not support querying optional values, return an error
      if (!validatedScratchpad.card.shouldQueryOptionalValues) {
        return toolResult(
          `Searching for values is not supported for signal: ${validatedScratchpad.card.signalDescription}.`,
        );
      }

      const nodes = sharedCampaigns.nodesRef.value;

      const allFeeds = Object.values(nodes)
        .map(getFeedIdFromNode)
        .filter((feedId) => feedId !== null);

      let validatedFeedIds: number[] = [];

      // Waterfall step 1: If feedIds provided from prompt, validate and return it
      if (!!feedIds?.length) {
        for (const feedId of feedIds) {
          if (allFeeds.includes(feedId)) {
            validatedFeedIds.push(feedId);
          }
        }
      } else {
        // Waterfall step 2: Check feeds from previous cards connected to this condition card
        validatedFeedIds = getFeedsFromPreviousCards(cardId);

        // Waterfall step 3: Check all feeds used in the campaign
        if (feedIds?.length === 0) {
          validatedFeedIds = Object.values(allFeeds).filter(Boolean);
        }
      }

      const result = await conditionsApi.searchFeedValues({
        feedIds: validatedFeedIds,
        signalKey: validatedScratchpad.card.condKey,
        valueContainsString: valueContainsString ?? undefined,
        maxResults: maxResults || MAX_RESULTS,
      });

      return toolResult(
        JSON.stringify({
          values: result.values,
          hasMore: result.hasMore,
          instructions: result.hasMore
            ? 'More values are available. Consider refining the search with valueContainsString if needed.'
            : '',
        }),
      );
    } catch (error) {
      return toolResult(
        `Error searching feed values: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.SearchFeedValues,
    displayName: 'Searching feed values',
    description:
      'Searches for values of a specific column (signal) from a feed. Use this tool when signal.shouldQueryOptionalValues is true and you need to find relevant values for condition outputs.',
    execute,
    inputSchema: searchFeedValuesToolSchema,
  });
}
